import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import type { MaintenanceReport } from "@/hooks/useMaintenanceReports";
import {
  buildClaimDraft,
  buildDoneLine,
  issueTypeLabel,
  normalizeIssueStatus,
  parseIssueTriageMeta,
  serializeIssueTriageMeta,
  type IssueTriageMeta,
  type IssueTriagePath,
  type IssueTriageStatus,
} from "@/lib/issueTriage";

export type IssueInboxRow = MaintenanceReport & {
  property_name: string;
  property_unit: string | null;
  job_date: string | null;
  cleaner_name: string | null;
  guest_name: string | null;
  guest_stay_start: string | null;
  guest_stay_end: string | null;
  reservation_external_id: string | null;
  triage: IssueTriageMeta;
  triage_status: IssueTriageStatus;
  type_label: "Missing" | "Broken";
};

function propertyLabel(name: string, unit: string | null | undefined): string {
  const n = (name || "Property").trim();
  const u = (unit || "").trim();
  return u ? `${n} · ${u}` : n;
}

export function formatIssuePropertyLabel(row: Pick<IssueInboxRow, "property_name" | "property_unit">): string {
  return propertyLabel(row.property_name, row.property_unit);
}

export function issueNote(row: Pick<MaintenanceReport, "notes" | "task_description">): string {
  const n = (row.notes || "").trim();
  if (n) return n;
  return (row.task_description || "Issue").trim();
}

function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    const d = parseISO(iso.length === 10 ? `${iso}T12:00:00` : iso);
    return format(d, "MMM d");
  } catch {
    return null;
  }
}

export function formatGuestStayLabel(start: string | null, end: string | null): string | null {
  const a = formatShortDate(start);
  const b = formatShortDate(end);
  if (a && b) return `${a}–${b}`;
  return a || b;
}

export function formatCleanLabel(jobDate: string | null | undefined): string {
  const d = formatShortDate(jobDate);
  return d ? `Clean · ${d}` : "Clean";
}

export function formatIssueMetaLine(row: IssueInboxRow): string {
  const parts = [formatIssuePropertyLabel(row)];
  if (row.cleaner_name?.trim()) parts.push(row.cleaner_name.trim());
  const d = formatShortDate(row.job_date);
  if (d) parts.push(d);
  return parts.join(" · ");
}

export function formatIssueDetailSubline(row: IssueInboxRow): string {
  const parts = [formatIssuePropertyLabel(row)];
  if (row.cleaner_name?.trim()) parts.push(row.cleaner_name.trim());
  const d = formatShortDate(row.job_date);
  if (d) parts.push(d);
  return parts.join(" · ");
}

export function formatIssueGuestLine(row: IssueInboxRow): string {
  const clean = formatShortDate(row.job_date);
  const cleanBit = clean ? `Clean ${clean}` : "Clean";
  if (row.guest_name?.trim()) {
    const stay = formatGuestStayLabel(row.guest_stay_start, row.guest_stay_end);
    return `${cleanBit} · guest ${row.guest_name.trim()}${stay ? ` · ${stay}` : ""}`;
  }
  return `${cleanBit} · no linked reservation`;
}

/** Cross-property or single-property Issues inbox (Slice 7 + 19). */
export function useIssuesInbox(propertyIds: string[] | undefined) {
  const idsKey = (propertyIds ?? []).slice().sort().join(",");
  return useQuery({
    queryKey: ["issues-inbox", idsKey],
    queryFn: async (): Promise<IssueInboxRow[]> => {
      if (!propertyIds?.length) return [];

      const { data: reports, error } = await supabase
        .from("maintenance_issue_reports")
        .select("*")
        .in("property_id", propertyIds)
        .in("response", ["no", "needs_repair"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      if (!reports?.length) return [];

      const propIds = [...new Set(reports.map((r) => r.property_id))];
      const taskIds = [
        ...new Set(reports.map((r) => r.cleaning_task_id).filter((id): id is string => !!id)),
      ];
      const cleanerIds = [...new Set(reports.map((r) => r.cleaner_id).filter(Boolean))];

      const [{ data: props }, { data: tasks }, { data: cleaners }] = await Promise.all([
        supabase.from("properties").select("id, name, unit").in("id", propIds),
        taskIds.length
          ? supabase
              .from("cleaning_tasks")
              .select("id, scheduled_date, completed_at, reservation_id")
              .in("id", taskIds)
          : Promise.resolve({
              data: [] as {
                id: string;
                scheduled_date: string | null;
                completed_at: string | null;
                reservation_id: string | null;
              }[],
            }),
        cleanerIds.length
          ? supabase.from("cleaners").select("id, name").in("id", cleanerIds)
          : Promise.resolve({ data: [] as { id: string; name: string }[] }),
      ]);

      const reservationIds = [
        ...new Set(
          (tasks ?? [])
            .map((t) => t.reservation_id)
            .filter((id): id is string => !!id),
        ),
      ];

      const { data: reservations } = reservationIds.length
        ? await supabase
            .from("reservations")
            .select("id, guest_name, start_date, end_date, external_id")
            .in("id", reservationIds)
        : { data: [] as { id: string; guest_name: string | null; start_date: string; end_date: string; external_id: string | null }[] };

      // Fallback: nearest prior reservation by property + job date when task has no link
      const needFallback = reports.filter((r) => {
        const t = r.cleaning_task_id
          ? (tasks ?? []).find((x) => x.id === r.cleaning_task_id)
          : undefined;
        return !t?.reservation_id;
      });
      const fallbackByReport = new Map<
        string,
        { guest_name: string | null; start_date: string; end_date: string; external_id: string | null }
      >();
      if (needFallback.length) {
        const { data: recentRes } = await supabase
          .from("reservations")
          .select("id, property_id, guest_name, start_date, end_date, external_id")
          .in("property_id", propIds)
          .order("end_date", { ascending: false })
          .limit(200);
        for (const report of needFallback) {
          const jobDay = (
            (report.cleaning_task_id
              ? (tasks ?? []).find((x) => x.id === report.cleaning_task_id)?.scheduled_date
              : null) ||
            report.created_at
          )?.slice(0, 10);
          const match = (recentRes ?? []).find(
            (r) =>
              r.property_id === report.property_id &&
              (!jobDay || r.end_date <= jobDay),
          );
          if (match) {
            fallbackByReport.set(report.id, {
              guest_name: match.guest_name,
              start_date: match.start_date,
              end_date: match.end_date,
              external_id: match.external_id,
            });
          }
        }
      }

      const propMap = new Map((props ?? []).map((p) => [p.id, p]));
      const taskMap = new Map((tasks ?? []).map((t) => [t.id, t]));
      const cleanerMap = new Map((cleaners ?? []).map((c) => [c.id, c]));
      const resMap = new Map((reservations ?? []).map((r) => [r.id, r]));

      return reports.map((report) => {
        const p = propMap.get(report.property_id);
        const t = report.cleaning_task_id ? taskMap.get(report.cleaning_task_id) : undefined;
        const linked = t?.reservation_id ? resMap.get(t.reservation_id) : undefined;
        const fallback = linked ? undefined : fallbackByReport.get(report.id);
        const guest = linked || fallback;
        const triage = parseIssueTriageMeta(report.resolution_note);
        const triage_status = normalizeIssueStatus(report.status);
        return {
          ...report,
          photos: report.photos ?? [],
          status: (report.status || "open") as MaintenanceReport["status"],
          property_name: p?.name ?? "Property",
          property_unit: p?.unit ?? null,
          job_date: t?.scheduled_date ?? t?.completed_at ?? report.created_at,
          cleaner_name: cleanerMap.get(report.cleaner_id)?.name ?? null,
          guest_name: guest?.guest_name ?? null,
          guest_stay_start: guest?.start_date ?? null,
          guest_stay_end: guest?.end_date ?? null,
          reservation_external_id: guest?.external_id ?? null,
          triage,
          triage_status,
          type_label: issueTypeLabel(report.response),
        } as IssueInboxRow;
      });
    },
    enabled: !!propertyIds && propertyIds.length > 0,
    staleTime: 60_000,
  });
}

function invalidateIssueQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  propertyId: string,
) {
  queryClient.invalidateQueries({ queryKey: ["issues-inbox"] });
  queryClient.invalidateQueries({ queryKey: ["maintenance-reports", propertyId] });
  queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
}

export function useMarkIssueDone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reportId,
      propertyId,
      resolvedBy,
      path,
      assigneeName,
      existingMeta,
    }: {
      reportId: string;
      propertyId: string;
      resolvedBy?: string;
      path?: IssueTriagePath | null;
      assigneeName?: string | null;
      existingMeta?: IssueTriageMeta;
    }) => {
      const whenLabel = format(new Date(), "MMM d");
      const meta: IssueTriageMeta = {
        ...(existingMeta ?? { v: 1 }),
        v: 1,
        path: path ?? existingMeta?.path ?? null,
        assignee_name: assigneeName ?? existingMeta?.assignee_name ?? null,
        done_line: buildDoneLine({
          path: path ?? existingMeta?.path,
          assigneeName: assigneeName ?? existingMeta?.assignee_name,
          whenLabel,
        }),
      };
      const { data, error } = await supabase
        .from("maintenance_issue_reports")
        .update({
          status: "resolved",
          resolved_at: new Date().toISOString(),
          resolved_by: resolvedBy || null,
          resolution_note: serializeIssueTriageMeta(meta),
        })
        .eq("id", reportId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        throw new Error("Update blocked — check you still have access to this property.");
      }
      return { data, propertyId };
    },
    onSuccess: (result) => invalidateIssueQueries(queryClient, result.propertyId),
  });
}

export function useUpdateIssueTriage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reportId,
      propertyId,
      status,
      meta,
      clearResolved,
    }: {
      reportId: string;
      propertyId: string;
      status: IssueTriageStatus;
      meta: IssueTriageMeta;
      clearResolved?: boolean;
    }) => {
      const patch: Record<string, unknown> = {
        status,
        resolution_note: serializeIssueTriageMeta({ ...meta, v: 1 }),
      };
      if (status === "resolved") {
        patch.resolved_at = new Date().toISOString();
      } else if (clearResolved) {
        patch.resolved_at = null;
        patch.resolved_by = null;
      }
      const { data, error } = await supabase
        .from("maintenance_issue_reports")
        .update(patch)
        .eq("id", reportId)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        throw new Error("Update blocked — check you still have access to this property.");
      }
      return { data, propertyId };
    },
    onSuccess: (result) => invalidateIssueQueries(queryClient, result.propertyId),
  });
}

export function draftForIssue(row: IssueInboxRow): string {
  if (row.triage.claim_draft?.trim()) return row.triage.claim_draft.trim();
  return buildClaimDraft({
    note: issueNote(row),
    room: row.room_name,
    type: row.type_label,
    cleanLabel: formatShortDate(row.job_date) ? `${formatShortDate(row.job_date)} turnover` : "turnover",
    guestName: row.guest_name,
    guestStayLabel: formatGuestStayLabel(row.guest_stay_start, row.guest_stay_end),
  });
}
